import { clamp } from "lodash";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

import { MatrixTableError } from "src/matrixTableNode/MatrixTableNodeEditor";

type RowCol = { row: number; col: number };

type MatrixTableNavState = {
  selectedCell: Nullable<RowCol>;
  editingCell: Nullable<RowCol>;
  hoveredCell: Nullable<RowCol>;
  errors: MatrixTableError[];
};

type MatrixTableNavActions = {
  selectCell: (selection: RowCol) => void;
  editCell: (selection: RowCol) => void;
  hoverCell: (selection: Nullable<RowCol>) => void;
  reset: (field?: keyof MatrixTableNavState) => void;
  enableEditingMode: () => void;
  moveColumn: (diff: number, maxColumn: number) => void;
  moveRow: (diff: 1 | -1, rowsCount: number) => void;
  setErrors: (errors: MatrixTableError[]) => void;
};

type MatrixTableNavStore = ZustandStore<
  MatrixTableNavState,
  MatrixTableNavActions
>;

const isSelectionExist = (
  state: MatrixTableNavStore,
): state is MatrixTableNavStore & { selectedCell: RowCol } =>
  state.selectedCell !== null;

const useMatrixTableNavStore = create(
  immer<MatrixTableNavStore>((set) => ({
    selectedCell: null,
    editingCell: null,
    hoveredCell: null,
    errors: [],

    actions: {
      hoverCell: (arg) =>
        set((state) => {
          state.hoveredCell = arg;
        }),

      selectCell: ({ row, col }) =>
        set((state) => {
          if (
            !(
              state.selectedCell?.row === row && state.selectedCell?.col === col
            )
          ) {
            state.selectedCell = { row, col };
            state.editingCell = null;
          }
        }),

      editCell: (cell) =>
        set((state) => {
          state.selectedCell = cell;
          state.editingCell = cell;
        }),

      reset: (field) =>
        field
          ? set({ [field]: null })
          : set({
              selectedCell: null,
              editingCell: null,
              hoveredCell: null,
            }),

      enableEditingMode: () => {
        set((state) => {
          // Enable editing mode only if some cell is selected
          if (isSelectionExist(state)) {
            state.editingCell = state.selectedCell;
          }
        });
      },

      moveColumn: (diff, maxColumn) =>
        set((state) => {
          if (state.selectedCell) {
            const { row: rowIndex, col: colIndex } = state.selectedCell;
            const nextColumnIndex = clamp(colIndex + diff, 0, maxColumn);

            state.selectedCell = {
              row: rowIndex,
              col: nextColumnIndex,
            };
          }
          state.editingCell = null;
        }),

      moveRow: (diff, rowsCount) =>
        set((state) => {
          if (state.selectedCell) {
            const { row: rowIndex, col: colId } = state.selectedCell;

            state.selectedCell = {
              row: clamp(rowIndex + diff, -1, rowsCount),
              col: colId,
            };
          }
          state.editingCell = null;
        }),

      setErrors: (errors) => {
        set((state) => {
          state.errors = errors;
        });
      },
    },
  })),
);

export const useMatrixTableNavActions = () =>
  useMatrixTableNavStore((state) => state.actions);

export const useMatrixTableCellSelected = (selection: RowCol) =>
  useMatrixTableNavStore(
    (state) =>
      state.selectedCell?.row === selection.row &&
      state.selectedCell?.col === selection.col,
  );

export const useMatrixTableCellEditing = (selection: RowCol) =>
  useMatrixTableNavStore(
    (state) =>
      state.editingCell?.row === selection.row &&
      state.editingCell?.col === selection.col,
  );

export const useMatrixTableMode = () =>
  useMatrixTableNavStore((state) => {
    if (state.editingCell) {
      return "edit";
    }

    return state.selectedCell ? "select" : "view";
  });

export const useMatrixTableError = ({ row, col }: RowCol) =>
  useMatrixTableNavStore(
    (state) =>
      state.errors.find((error) => error.row === row && error.col === col)
        ?.message,
  );

export const useMatrixTableCellHighlighted = ({ row, col }: RowCol) =>
  useMatrixTableNavStore((state) => {
    if (!state.selectedCell) {
      return false;
    }

    if (row < 0 || col < 0) {
      return false;
    }

    return (
      (state.selectedCell.row === row && col <= state.selectedCell.col) ||
      (state.selectedCell.col === col && row <= state.selectedCell.row)
    );
  });

export const useMatrixTableRowHovered = (row: number) =>
  useMatrixTableNavStore((state) => state.hoveredCell?.row === row);

export const useMatrixTableColHovered = (col: number) =>
  useMatrixTableNavStore((state) => state.hoveredCell?.col === col);
